/**
 * Shared token refresh logic with deduplication.
 *
 * Used by the global fetch interceptor to refresh expired access tokens.
 * Concurrent refresh requests are coalesced into a single /auth/refresh call.
 *
 * @module
 * @see docs/api/auth.md
 */

import { API_URL } from '../config';

let refreshPromise: Promise<boolean> | null = null;

/** Set to true when refresh returns 403 "Account deactivated" */
export let accountDeactivatedDuringRefresh = false;

/** The unintercepted fetch — set by the interceptor at install time to avoid recursion. */
let originalFetch: typeof window.fetch = window.fetch.bind(window);

export function setOriginalFetch(fn: typeof window.fetch): void {
  originalFetch = fn;
}

/**
 * Attempt to refresh the access token via POST /auth/refresh.
 *
 * Concurrent calls are deduplicated — only one /auth/refresh request
 * is made regardless of how many callers await this function.
 *
 * @returns true if the refresh succeeded, false otherwise
 */
export function attemptTokenRefresh(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await originalFetch(API_URL + '/auth/refresh', {
          method: 'POST',
          credentials: 'include',
          signal: AbortSignal.timeout(10_000),
        });
        if (res.status === 403) {
          const body = await res.json().catch(() => ({}));
          if (body?.error === 'Account deactivated') {
            accountDeactivatedDuringRefresh = true;
          }
        }
        return res.ok;
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('[auth-refresh] Token refresh failed:', err);
        }
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}
